export const MAX_IMAGE_FILE_SIZE_BYTES = 2 * 1024 * 1024;
export const MAX_IMAGE_DATA_URL_BYTES = 2 * 1024 * 1024;

const MAX_SOURCE_IMAGE_FILE_SIZE_BYTES = 80 * 1024 * 1024;
const DEFAULT_MAX_IMAGE_SIDE_PX = 1920;
const MIN_IMAGE_SIDE_PX = 720;
const JPEG_MIME_TYPE = "image/jpeg";
const INITIAL_JPEG_QUALITY = 0.84;
const MIN_JPEG_QUALITY = 0.62;
const QUALITY_STEP = 0.08;
const MAX_COMPRESSION_ATTEMPTS = 14;

export interface PrepareImageFileDataUrlOptions {
  maxDataUrlBytes?: number;
  maxOutputBytes?: number;
  maxSourceBytes?: number;
  maxSidePx?: number;
}

interface ImageDimensions {
  width: number;
  height: number;
}

function isImageFile(file: File): boolean {
  if (file.type.startsWith("image/")) {
    return true;
  }

  return /\.(avif|bmp|gif|heic|heif|jpe?g|png|webp)$/i.test(file.name);
}

function formatFileSize(bytes: number): string {
  const megabytes = bytes / (1024 * 1024);
  return `${Number.isInteger(megabytes) ? megabytes : megabytes.toFixed(1)}MB`;
}

function normalizePositiveInteger(value: number | undefined, fallback: number) {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return fallback;
  }

  return Math.floor(value);
}

export function getDataUrlByteLength(dataUrl: string): number {
  return dataUrl.length;
}

function getDataUrlHeaderByteLength(mimeType: string): number {
  return `data:${mimeType};base64,`.length;
}

function getMaxBlobBytesForDataUrl(
  maxDataUrlBytes: number,
  mimeType = JPEG_MIME_TYPE,
): number {
  const payloadBytes = maxDataUrlBytes - getDataUrlHeaderByteLength(mimeType);
  if (payloadBytes <= 0) {
    return 0;
  }

  return Math.floor(payloadBytes / 4) * 3;
}

function estimateDataUrlByteLength(
  blobBytes: number,
  mimeType = JPEG_MIME_TYPE,
): number {
  return getDataUrlHeaderByteLength(mimeType) + Math.ceil(blobBytes / 3) * 4;
}

function resolveMaxOutputBytes(
  maxOutputBytes: number,
  maxDataUrlBytes: number,
): number {
  return Math.max(
    1,
    Math.min(maxOutputBytes, getMaxBlobBytesForDataUrl(maxDataUrlBytes)),
  );
}

export function validateImageFile(
  file: File,
  options: PrepareImageFileDataUrlOptions = {},
): string | null {
  if (!isImageFile(file)) {
    return "Можно загрузить только изображение";
  }

  const maxSourceBytes = normalizePositiveInteger(
    options.maxSourceBytes,
    MAX_SOURCE_IMAGE_FILE_SIZE_BYTES,
  );
  if (file.size > maxSourceBytes) {
    return `Исходный файл слишком большой. Максимум ${formatFileSize(maxSourceBytes)} до сжатия`;
  }

  return null;
}

export function readFileAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Некорректный формат файла"));
    };
    reader.onerror = () => reject(new Error("Не удалось прочитать файл"));
    reader.readAsDataURL(file);
  });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const imageUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(imageUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error("Не удалось обработать изображение"));
    };
    image.src = imageUrl;
  });
}

export function fitImageDimensions(
  dimensions: ImageDimensions,
  maxSidePx: number,
): ImageDimensions {
  const maxSide = Math.max(dimensions.width, dimensions.height);
  if (maxSide <= maxSidePx) {
    return dimensions;
  }

  const ratio = maxSidePx / maxSide;
  return {
    width: Math.max(1, Math.round(dimensions.width * ratio)),
    height: Math.max(1, Math.round(dimensions.height * ratio)),
  };
}

function createImageCanvas(
  image: HTMLImageElement,
  dimensions: ImageDimensions,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Браузер не поддерживает обработку изображений");
  }

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, dimensions.width, dimensions.height);
  context.drawImage(image, 0, 0, dimensions.width, dimensions.height);

  return canvas;
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error("Не удалось сжать изображение"));
      },
      JPEG_MIME_TYPE,
      quality,
    );
  });
}

async function renderCompressedImage(
  image: HTMLImageElement,
  dimensions: ImageDimensions,
  quality: number,
): Promise<Blob> {
  const canvas = createImageCanvas(image, dimensions);
  return canvasToBlob(canvas, quality);
}

function reduceDimensions(
  dimensions: ImageDimensions,
  blobSize: number,
  maxOutputBytes: number,
) {
  const maxSide = Math.max(dimensions.width, dimensions.height);
  if (maxSide <= MIN_IMAGE_SIDE_PX) {
    return dimensions;
  }

  const sizeRatio = Math.sqrt(maxOutputBytes / blobSize) * 0.92;
  const ratio = Math.min(0.86, Math.max(0.62, sizeRatio));
  const nextMaxSide = Math.max(
    MIN_IMAGE_SIDE_PX,
    Math.floor(maxSide * ratio),
  );

  return fitImageDimensions(dimensions, nextMaxSide);
}

async function compressImageToDataUrl(
  file: File,
  maxOutputBytes: number,
  maxSidePx: number,
  maxDataUrlBytes: number,
): Promise<string> {
  const image = await loadImage(file);
  const sourceDimensions = {
    width: image.naturalWidth,
    height: image.naturalHeight,
  };

  if (sourceDimensions.width <= 0 || sourceDimensions.height <= 0) {
    throw new Error("Некорректный размер изображения");
  }

  let dimensions = fitImageDimensions(sourceDimensions, maxSidePx);
  let quality = INITIAL_JPEG_QUALITY;
  let bestBlob: Blob | null = null;

  for (let attempt = 0; attempt < MAX_COMPRESSION_ATTEMPTS; attempt += 1) {
    const blob = await renderCompressedImage(image, dimensions, quality);
    if (!bestBlob || blob.size < bestBlob.size) {
      bestBlob = blob;
    }

    if (blob.size <= maxOutputBytes) {
      const dataUrl = await readFileAsDataUrl(blob);
      if (getDataUrlByteLength(dataUrl) <= maxDataUrlBytes) {
        return dataUrl;
      }
    }

    if (quality > MIN_JPEG_QUALITY) {
      quality = Math.max(MIN_JPEG_QUALITY, quality - QUALITY_STEP);
      continue;
    }

    const nextDimensions = reduceDimensions(
      dimensions,
      blob.size,
      maxOutputBytes,
    );
    if (
      nextDimensions.width === dimensions.width &&
      nextDimensions.height === dimensions.height
    ) {
      break;
    }

    dimensions = nextDimensions;
    quality = INITIAL_JPEG_QUALITY;
  }

  if (bestBlob && bestBlob.size <= maxOutputBytes) {
    const dataUrl = await readFileAsDataUrl(bestBlob);
    if (getDataUrlByteLength(dataUrl) <= maxDataUrlBytes) {
      return dataUrl;
    }
  }

  throw new Error(
    `Не удалось сжать изображение до ${formatFileSize(maxDataUrlBytes)} для отправки`,
  );
}

export async function prepareImageFileDataUrl(
  file: File,
  options: PrepareImageFileDataUrlOptions = {},
): Promise<string> {
  const validationError = validateImageFile(file, options);
  if (validationError) {
    throw new Error(validationError);
  }

  const maxDataUrlBytes = normalizePositiveInteger(
    options.maxDataUrlBytes,
    MAX_IMAGE_DATA_URL_BYTES,
  );
  const requestedMaxOutputBytes = normalizePositiveInteger(
    options.maxOutputBytes,
    MAX_IMAGE_FILE_SIZE_BYTES,
  );
  const maxOutputBytes = resolveMaxOutputBytes(
    requestedMaxOutputBytes,
    maxDataUrlBytes,
  );
  const originalMimeType = file.type || JPEG_MIME_TYPE;
  if (
    file.size <= maxOutputBytes &&
    estimateDataUrlByteLength(file.size, originalMimeType) <= maxDataUrlBytes
  ) {
    const dataUrl = await readFileAsDataUrl(file);
    if (getDataUrlByteLength(dataUrl) <= maxDataUrlBytes) {
      return dataUrl;
    }
  }

  const maxSidePx = normalizePositiveInteger(
    options.maxSidePx,
    DEFAULT_MAX_IMAGE_SIDE_PX,
  );

  return compressImageToDataUrl(
    file,
    maxOutputBytes,
    maxSidePx,
    maxDataUrlBytes,
  );
}
